import React, {useEffect, useRef, useState} from 'react';
import {useLazyQuery, useMutation} from '@apollo/client';
import {GET_AUTHNET_HOSTED_PAYMENT_PARAMS, SYNC_AUTHNET_HOSTED_FORM} from './checkoutQueries';

const AcceptHostedIframe = ({
    cartData,
    customerInfo,
    onTransactionComplete,
    onTransactionError,
    onCancel,
    isVisible = false
}) => {
    const [hostedPaymentToken, setHostedPaymentToken] = useState(null);
    const [hostedPaymentParams, setHostedPaymentParams] = useState(null);
    const [iframeSessionId, setIframeSessionId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [iframeHeight, setIframeHeight] = useState(650);
    const [formUrl, setFormUrl] = useState('');
    const iframeRef = useRef(null);
    const formRef = useRef(null);

    // GraphQL queries/mutations
    const [getHostedParamsQuery] = useLazyQuery(GET_AUTHNET_HOSTED_PAYMENT_PARAMS);
    const [syncHostedFormMutation] = useMutation(SYNC_AUTHNET_HOSTED_FORM);

    // Parse query string helper
    const parseQueryString = (str) => {
        const vars = str.split('&');
        const query = {};

        for (let i = 0; i < vars.length; i++) {
            const pair = vars[i].split('=');
            query[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1] || '');
        }

        return query;
    };

    // Communication handler for iframe messages
    useEffect(() => {
        // Set up global communication handler at the correct window level
        const communicationHandler = {
            onReceiveCommunication: function(argument) {
                let params = {};

                if (argument && argument.qstr) {
                    params = parseQueryString(argument.qstr);
                } else if (typeof argument === 'string') {
                    params = parseQueryString(argument);
                } else if (argument && typeof argument === 'object') {
                    params = argument;
                }

                switch (params.action) {
                    case 'resizeWindow':
                        const height = parseInt(params.height);
                        if (height && height > 0) {
                            setIframeHeight(height);
                        }
                        break;

                    case 'cancel':
                        setError('Payment was cancelled');
                        setIsLoading(false);
                        if (onCancel) onCancel();
                        break;

                    case 'transactResponse':
                        setIsLoading(false); // Stop loading immediately
                        handleTransactionResponse(params);
                        break;

                    default:
                        // Check if it's a direct transaction response without action
                        if (params.response_code || params.trans_id) {
                            setIsLoading(false);
                            handleTransactionResponse(params);
                        }
                }
            }
        };

        // Set handler at multiple window levels to ensure iframe can reach it
        window.CommunicationHandler = communicationHandler;

        // Also set at parent level if it exists and is different
        if (window.parent && window.parent !== window) {
            window.parent.CommunicationHandler = communicationHandler;
        }

        // Set at top level for iframe communicator that expects window.parent.parent
        if (window.top && window.top !== window) {
            window.top.CommunicationHandler = communicationHandler;
        }

        // Also listen for direct window messages (ParadoxLabs communicator uses postMessage)
        const handleWindowMessage = (event) => {
            // Handle both object and string data from communicator
            if (event.data) {
                // Check if this is from Authorize.Net domains OR our own domain (proxy)
                if (event.origin === 'https://test.authorize.net' ||
                    event.origin === 'https://accept.authorize.net' ||
                    event.origin.includes('authorize.net') ||
                    event.origin === 'https://arbsession.loc:3000' ||
                    event.origin === window.location.origin) {

                    // If data is a string, try to parse it as query string
                    let messageData = event.data;
                    if (typeof event.data === 'string') {
                        try {
                            // Try parsing as query string first
                            messageData = parseQueryString(event.data);
                        } catch (e) {
                            // If parsing fails, treat as action string
                            messageData = { action: event.data };
                        }
                    }

                    // Handle different message types using parsed messageData
                    if (messageData.action === 'transactResponse') {
                        handleTransactionResponse(messageData);
                    } else if (messageData.response_code) {
                        // Sometimes the response comes directly without action field
                        handleTransactionResponse(messageData);
                    } else if (messageData.action === 'resizeWindow') {
                        // Handle resize if needed
                    } else {
                        // Handle other message types
                        if (window.CommunicationHandler && window.CommunicationHandler.onReceiveCommunication) {
                            window.CommunicationHandler.onReceiveCommunication(messageData);
                        }

                        // Send acknowledgment back to iframe to prevent processing from getting stuck
                        if (event.source && typeof event.source.postMessage === 'function') {
                            try {
                                event.source.postMessage({ received: true, originalData: messageData }, event.origin);
                            } catch (e) {
                                console.log('Could not send acknowledgment back to iframe:', e);
                            }
                        }
                    }
                }
            }
        };

        window.addEventListener('message', handleWindowMessage, false);

        // Cleanup
        return () => {
            if (window.CommunicationHandler) {
                delete window.CommunicationHandler;
            }
            if (window.parent && window.parent !== window && window.parent.CommunicationHandler) {
                delete window.parent.CommunicationHandler;
            }
            if (window.top && window.top !== window && window.top.CommunicationHandler) {
                delete window.top.CommunicationHandler;
            }
            window.removeEventListener('message', handleWindowMessage, false);
        };
    }, [onTransactionComplete, onTransactionError, onCancel]);

    // Handle transaction response from iframe
    const handleTransactionResponse = async (params) => {
        try {
            // Parse transaction response - check if it's in JSON format
            let responseData = params;

            // If response is a JSON string, parse it
            if (params.response && typeof params.response === 'string') {
                try {
                    responseData = JSON.parse(params.response);
                } catch (e) {
                    console.warn('Failed to parse JSON response:', e);
                    responseData = params;
                }
            }

            // Parse transaction response from Authorize.Net
            const response = {
                responseCode: responseData.responseCode || responseData.response_code || params.response_code,
                transactionId: responseData.transId || responseData.trans_id || responseData.transactionId || params.trans_id,
                authCode: responseData.authorization || responseData.auth_code || responseData.authCode || params.auth_code,
                messageCode: responseData.messageCode || responseData.message_code || params.message_code,
                description: responseData.description || params.description,
                accountNumber: responseData.accountNumber || responseData.account_number || params.account_number,
                accountType: responseData.accountType || responseData.account_type || params.account_type,
                amount: responseData.totalAmount || responseData.amount || params.amount,
                invoiceNumber: responseData.orderInvoiceNumber || responseData.invoice_number || params.invoice_number,
                customerId: responseData.customerId,
                dateTime: responseData.dateTime,
                shipTo: responseData.shipTo
            };

            // Check for success - multiple possible indicators
            const isSuccess =
                params.response_code === '1' ||
                params.responseCode === '1' ||
                params.response === '1' ||
                response.transactionId; // If we have a transaction ID, assume success

            if (isSuccess) {
                // Successful transaction
                setIsLoading(false);

                if (onTransactionComplete) {
                    onTransactionComplete({
                        success: true,
                        transactionId: response.transactionId,
                        authCode: response.authCode,
                        amount: response.amount,
                        response: response,
                        iframeSessionId: iframeSessionId
                    });
                }
            } else {
                // Failed transaction
                const errorMessage = response.description || params.description || 'Transaction failed';
                console.error('Transaction failed:', errorMessage);
                setError(errorMessage);
                setIsLoading(false);

                if (onTransactionError) {
                    onTransactionError({
                        success: false,
                        error: errorMessage,
                        response: response
                    });
                }
            }

        } catch (err) {
            console.error('Error parsing transaction response:', err);
            const errorMessage = 'Error processing payment response';
            setError(errorMessage);
            setIsLoading(false);

            if (onTransactionError) {
                onTransactionError({
                    success: false,
                    error: errorMessage,
                    parsingError: true,
                    response: null
                });
            }
        }
    };

    // Request hosted payment form parameters from Magento GraphQL API
    const requestHostedPaymentToken = async () => {
        if (!cartData?.id) {
            setError('Cart ID is not available. Please refresh the page and try again.');
            return null;
        }

        setIsLoading(true);
        setError(null);

        try {
            const result = await getHostedParamsQuery({
                variables: {
                    cartId: cartData.id,
                    guestEmail: customerInfo?.email || null
                }
            });

            const data = result.data?.authnetcimHostedPaymentFormParams;

            if (data?.iframeAction && data?.iframeParams) {
                setFormUrl(data.iframeAction);
                setIframeSessionId(data.iframeSessionId);

                // Parse iframeParams JSON string to extract parameters
                let params = {};
                try {
                    params = JSON.parse(data.iframeParams);
                } catch (e) {
                    console.warn('Could not parse iframe params:', data.iframeParams);
                    // If it's not JSON, assume it's a single token value
                    params = { token: data.iframeParams };
                }

                // Add the iframe communicator URL to the parameters
                params.iframeCommunicatorUrl = `${window.location.protocol}//${window.location.host}/authnetcim/hosted/communicator`;

                setHostedPaymentParams(params);
                setHostedPaymentToken(params.token || Object.keys(params)[0]); // Use first param as token

                return true;
            } else {
                throw new Error('Failed to get hosted payment parameters');
            }

        } catch (err) {
            console.error('Error requesting hosted payment parameters:', err);

            let errorMessage = 'Failed to initialize secure payment form. ';

            if (err.graphQLErrors?.length > 0) {
                errorMessage += err.graphQLErrors[0].message;
            } else if (err.networkError) {
                errorMessage += 'Network error. Please check your connection and try again.';
            } else {
                errorMessage += err.message || 'Unknown error occurred.';
            }

            setError(errorMessage);
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    // Initialize hosted payment form when visible
    useEffect(() => {
        if (isVisible && !hostedPaymentParams && !isLoading) {
            requestHostedPaymentToken();
        }
    }, [isVisible]);

    // Submit parameters to iframe when available
    useEffect(() => {
        if (hostedPaymentParams && formRef.current && iframeRef.current && formUrl) {
            // Small delay to ensure iframe is ready
            setTimeout(() => {
                formRef.current.submit();
            }, 100);
        }
    }, [hostedPaymentParams, formUrl]);

    if (!isVisible) {
        return null;
    }

    return (
        <div className="accept-hosted-container">
            {error && (
                <div className="error-message" style={{
                    marginBottom: '1rem',
                    padding: '1rem',
                    backgroundColor: '#fee',
                    border: '1px solid #fcc',
                    borderRadius: '4px',
                    color: '#c33'
                }}>
                    {error}
                    <button
                        type="button"
                        onClick={() => {
                            setError(null);
                            setHostedPaymentToken(null);
                            setHostedPaymentParams(null);
                            requestHostedPaymentToken();
                        }}
                        style={{
                            marginLeft: '1rem',
                            padding: '0.25rem 0.5rem',
                            backgroundColor: 'transparent',
                            border: '1px solid #c33',
                            borderRadius: '3px',
                            color: '#c33',
                            cursor: 'pointer',
                            fontSize: '0.875rem'
                        }}
                    >
                        Try Again
                    </button>
                </div>
            )}

            {isLoading && (
                <div className="loading-container" style={{
                    textAlign: 'center',
                    padding: '3rem 2rem',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '8px',
                    border: '1px solid #eee'
                }}>
                    <div className="loading-spinner" style={{
                        border: '4px solid #f3f3f3',
                        borderTop: '4px solid #3498db',
                        borderRadius: '50%',
                        width: '40px',
                        height: '40px',
                        animation: 'spin 2s linear infinite',
                        margin: '0 auto 1rem'
                    }} />
                    <p style={{ margin: 0, color: '#666' }}>
                        {hostedPaymentParams ? 'Processing payment...' : 'Initializing secure payment form...'}
                    </p>
                    <small style={{ color: '#999', display: 'block', marginTop: '0.5rem' }}>
                        This may take a few moments
                    </small>
                </div>
            )}

            {hostedPaymentParams && formUrl && !isLoading && (
                <div className="iframe-container">
                    <div className="security-notice" style={{
                        background: '#e8f4fd',
                        border: '1px solid #bee5eb',
                        borderRadius: '8px',
                        padding: '1rem',
                        marginBottom: '1rem',
                        textAlign: 'center'
                    }}>
                        <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>🔒</div>
                        <strong>Secure Payment Form</strong>
                        <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: '#0c5460' }}>
                            This payment form is hosted and secured by Authorize.Net with 256-bit SSL encryption.
                            Your payment information is processed securely through Magento and never stored on our servers.
                        </p>
                    </div>

                    <iframe
                        ref={iframeRef}
                        id="accept_hosted_iframe"
                        name="accept_hosted_iframe"
                        width="100%"
                        height={`${iframeHeight}px`}
                        frameBorder="0"
                        scrolling="no"
                        title="Authorize.Net Secure Payment Form"
                        style={{
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            minHeight: '500px',
                            backgroundColor: '#fff'
                        }}
                    />

                    <form
                        ref={formRef}
                        id="hosted_payment_form"
                        action={formUrl}
                        method="post"
                        target="accept_hosted_iframe"
                        style={{ display: 'none' }}
                    >
                        {hostedPaymentParams && Object.entries(hostedPaymentParams).map(([key, value]) => (
                            <input
                                key={key}
                                type="hidden"
                                name={key}
                                value={value}
                            />
                        ))}
                    </form>
                </div>
            )}


            <div className="payment-footer" style={{
                marginTop: '1rem',
                fontSize: '0.875rem',
                color: '#6b7280',
                textAlign: 'center',
                borderTop: '1px solid #eee',
                paddingTop: '1rem'
            }}>
                <p style={{ margin: 0 }}>
                    🔒 Protected by Authorize.Net • Integrated with Magento • PCI DSS Compliant
                </p>
            </div>

            <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
        </div>
    );
};

export default AcceptHostedIframe;